import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { 
  Facebook, 
  Instagram, 
  Linkedin, 
  Youtube, 
  Phone, 
  Mail, 
  Check, 
  Maximize2, 
  MessageCircle,
  Send,
  Music2,
  Twitter,
  MessageSquare,
  Ghost,
  AtSign,
  Globe,
  Twitch
} from "lucide-react";
import { Database } from "@/integrations/supabase/types";
import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type Profile = Database["public"]["Tables"]["profiles"]["Row"] & { email?: string };

interface ProfileHeaderProps {
  profile: Profile;
  isPublic?: boolean;
  onViewListings?: () => void;
}

export const ProfileHeader = ({ profile, isPublic, onViewListings }: ProfileHeaderProps) => {
  const [copied, setCopied] = useState(false);
  const [imageOpen, setImageOpen] = useState(false);

  const primaryColor = profile.primary_color || '#4B89FF';
  const secondaryColor = profile.secondary_color || '#7E69AB';

  const handleEmailClick = async () => {
    if (!profile.email) return;
    
    try {
      await navigator.clipboard.writeText(profile.email);
      setCopied(true);
      toast.success("Email copied to clipboard!");
      
      setTimeout(() => setCopied(false), 2000);
      
      window.location.href = `mailto:${profile.email}`;
    } catch (err) {
      console.error("Failed to copy email:", err);
      toast.error("Failed to copy email");
    }
  };

  const socialMediaPlatforms = [
    { 
      url: profile.website_url, 
      Icon: Globe, 
      label: 'Website',
      hoverColor: '#0ea5e9'
    },
    { 
      url: profile.facebook_url, 
      Icon: Facebook, 
      label: 'Facebook',
      hoverColor: '#1877f2'
    },
    { 
      url: profile.instagram_url, 
      Icon: Instagram, 
      label: 'Instagram',
      hoverColor: '#e4405f'
    },
    { 
      url: profile.linkedin_url, 
      Icon: Linkedin, 
      label: 'LinkedIn',
      hoverColor: '#0077b5'
    },
    { 
      url: profile.youtube_url, 
      Icon: Youtube, 
      label: 'YouTube',
      hoverColor: '#ff0000'
    },
    { 
      url: profile.tiktok_url, 
      Icon: Music2, 
      label: 'TikTok',
      hoverColor: '#000000'
    },
    { 
      url: profile.telegram_url, 
      Icon: Send, 
      label: 'Telegram',
      hoverColor: '#0088cc'
    },
    { 
      url: profile.whatsapp_url, 
      Icon: MessageCircle, 
      label: 'WhatsApp',
      hoverColor: '#25d366'
    },
    { 
      url: profile.twitter_url, 
      Icon: Twitter, 
      label: 'Twitter',
      hoverColor: '#1da1f2'
    },
    { 
      url: profile.threads_url, 
      Icon: AtSign, 
      label: 'Threads',
      hoverColor: '#000000'
    },
    { 
      url: profile.discord_url, 
      Icon: MessageSquare, 
      label: 'Discord',
      hoverColor: '#5865f2'
    },
    { 
      url: profile.reddit_url, 
      Icon: MessageCircle, 
      label: 'Reddit',
      hoverColor: '#ff4500'
    },
    { 
      url: profile.twitch_url, 
      Icon: Twitch, 
      label: 'Twitch',
      hoverColor: '#9146ff'
    },
    { 
      url: profile.snapchat_url, 
      Icon: Ghost, 
      label: 'Snapchat',
      hoverColor: '#fffc00'
    },
  ].filter(platform => platform.url);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative group"
    >
      <div 
        className="absolute inset-0 rounded-3xl overflow-hidden"
        style={{
          background: `radial-gradient(ellipse at top, ${primaryColor}20, ${secondaryColor}10, #ffffff)`
        }}
      >
        <motion.div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iYSIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVHJhbnNmb3JtPSJyb3RhdGUoNDUgNTAgNTApIj48cGF0aCBkPSJNMCAwaDQwdjQwSDB6IiBmaWxsPSJub25lIi8+PHBhdGggZD0iTTAgMGg0MHY0MEgweiIgZmlsbD0JcmVzdG9yIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2EpIi8+PC9zdmc+')`
          }}
          animate={{
            backgroundPosition: ["0% 0%", "100% 100%"],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse",
          }}
        />
      </div>
      
      <div className="relative bg-white/60 backdrop-blur-xl rounded-3xl shadow-xl transition-all duration-300 group-hover:shadow-2xl border border-blue-100/50">
        <div className="flex flex-col items-center text-center p-4 sm:p-6 md:p-8 lg:p-12 space-y-4 sm:space-y-6">
          <motion.div 
            className="relative"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <div className="relative inline-block">
              <Avatar className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 ring-4 ring-blue-200/80 shadow-xl transition-transform duration-300">
                <AvatarImage 
                  src={profile.avatar_url || undefined} 
                  alt={profile.full_name}
                  className="object-cover"
                />
                <AvatarFallback 
                  className="text-2xl sm:text-3xl md:text-4xl font-medium text-white"
                  style={{ background: `linear-gradient(to bottom right, ${primaryColor}, ${secondaryColor})` }}
                >
                  {profile.full_name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <motion.div 
                className="absolute -bottom-1 -right-1 h-6 w-6 sm:h-8 sm:w-8 rounded-full ring-[3px] ring-white shadow-lg flex items-center justify-center"
                style={{ background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` }}
                initial={{ rotate: -12 }}
                whileHover={{ rotate: 0, scale: 1.1 }}
                transition={{ duration: 0.2 }}
              >
                <Check className="h-3 w-3 sm:h-4 sm:w-4 text-white drop-shadow" />
              </motion.div>
            </div>

            <motion.div
              className="absolute inset-0 rounded-full cursor-pointer"
              onClick={() => setImageOpen(true)}
              whileHover={{ backgroundColor: "rgba(0,0,0,0.1)" }}
              transition={{ duration: 0.2 }}
            >
              <div className="h-full w-full flex items-center justify-center">
                <Maximize2 className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-lg" />
              </div>
            </motion.div>
          </motion.div>

          <Dialog open={imageOpen} onOpenChange={setImageOpen}>
            <DialogContent className="max-w-screen-md p-0 overflow-hidden bg-black/95">
              <img
                src={profile.avatar_url || undefined}
                alt={profile.full_name}
                className="w-full h-full object-contain"
              />
            </DialogContent>
          </Dialog>

          <div className="space-y-2 sm:space-y-3">
            <motion.h1 
              className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight [text-wrap:balance] px-4"
              style={{
                background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundSize: '200% auto'
              }}
            >
              {profile.full_name}
            </motion.h1>
            {profile.position && profile.company && (
              <motion.div className="space-y-1">
                <p className="text-base sm:text-lg md:text-xl tracking-wide text-gray-700 font-medium">
                  {profile.position}
                </p>
                <p className="text-sm sm:text-base text-gray-500">
                  at{' '}
                  <span style={{
                    background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    {profile.company}
                  </span>
                </p>
              </motion.div>
            )}
          </div>

          <motion.div 
            className="flex flex-wrap justify-center items-center gap-2 sm:gap-3 px-4 w-full"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            {profile.phone && (
              <motion.a 
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                href={`tel:${profile.phone}`}
                className="flex-1 min-w-[200px] max-w-[300px] inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-white shadow-lg hover:shadow-xl transition-all duration-300 text-sm sm:text-base font-semibold tracking-wide"
                style={{
                  background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})`,
                  boxShadow: `0 4px 6px -1px ${primaryColor}25, 0 2px 4px -1px ${primaryColor}10`
                }}
              >
                <Phone className="h-4 w-4" />
                {profile.phone}
              </motion.a>
            )}
            {profile.email && (
              <motion.button 
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleEmailClick}
                className="flex-1 min-w-[200px] max-w-[300px] inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-white shadow-lg hover:shadow-xl transition-all duration-300 text-sm sm:text-base font-semibold tracking-wide"
                style={{
                  background: `linear-gradient(to right, ${secondaryColor}, ${primaryColor})`,
                  boxShadow: `0 4px 6px -1px ${secondaryColor}25, 0 2px 4px -1px ${secondaryColor}10`
                }}
              >
                {copied ? (
                  <Check className="h-4 w-4 text-white" />
                ) : (
                  <Mail className="h-4 w-4" />
                )}
                {profile.email}
              </motion.button>
            )}
          </motion.div>

          {socialMediaPlatforms.length > 0 && (
            <motion.div 
              className="flex flex-wrap gap-2 sm:gap-3 justify-center pt-2 px-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              {socialMediaPlatforms.map((platform, index) => (
                <TooltipProvider key={index} delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <motion.div className="relative">
                        <motion.a
                          href={platform.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="relative p-2.5 sm:p-3 rounded-full text-white shadow-lg hover:shadow-xl transition-all duration-300 block"
                          style={{
                            background: index % 2 === 0 
                              ? `linear-gradient(to right, ${primaryColor}, ${secondaryColor})`
                              : `linear-gradient(to right, ${secondaryColor}, ${primaryColor})`,
                            boxShadow: `0 4px 6px -1px ${primaryColor}25, 0 2px 4px -1px ${primaryColor}10`
                          }}
                          whileHover={{ 
                            scale: 1.1,
                            y: -2,
                            background: platform.hoverColor,
                            boxShadow: `0 8px 16px -4px ${platform.hoverColor}40`
                          }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <platform.Icon 
                            className="w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-300" 
                            aria-label={platform.label}
                          />
                        </motion.a>
                      </motion.div>
                    </TooltipTrigger>
                    <TooltipContent 
                      side="top" 
                      className="bg-white/90 backdrop-blur-sm text-xs font-medium text-gray-600"
                      sideOffset={5}
                    >
                      {platform.label}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};
